import {
  Component,
  ChangeDetectorRef,
  computed,
  signal,
  ViewChild,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, EMPTY } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { TableComponent } from '@view/shared/molecules/app-table/app-table.component';
import { PaginatorComponent } from '@view/shared/molecules/paginator/paginator.component';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import {
  ButtonTypeEnum,
  ButtonSizeEnum
} from '@view/shared/molecules/button/buttonTypesEnum';
import { TableActionType } from '@view/shared/molecules/app-table/tableActionTypes';
import { IngestedOrder } from '@model/ingested-order/ingested-order.model';
import { IngestedOrdersPresenter } from '../../presenters/ingested-orders.presenter';
import { IOrderMetricsPort } from '../../../../domain/service/preclinical/order-metrics.port';
import { IFilterOptionsQueryPort } from '../../../../domain/service/preclinical/filter-options-query.port';
import { MetricsDashboardComponent } from '@view/shared/molecules/metrics-dashboard/metrics-dashboard.component';
import { ToastComponent } from '@view/shared/molecules/toast/toast.component';
import { ToastService } from '@view/shared/molecules/toast/service/toast.service';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';
import { OrderStatus } from '../../../../domain/types/order-status.type';
import { IngestedOrdersQueryParams } from '@model/ingested-order/ingested-order.model';
import {
  ColumnOption,
  FrozenColumnsSidebarComponent
} from '@view/preclinical/molecules/frozen-columns-sidebar/frozen-columns-sidebar.component';
import { FilterSidebarComponent } from '../../molecules/filter-sidebar/filter-sidebar.component';
import { ActiveFiltersComponent } from '@view/shared/molecules/active-filters/active-filters.component';
import {
  ExportConfirmationModalComponent,
  ExportModalState
} from '../../molecules/export-confirmation-modal/export-confirmation-modal.component';
import { BaseOrdersListComponent } from '../base-orders-list.component';
import { FrozenColumnsService } from '@helpers/table/frozen-columns.service';
import { SearchControlsComponent } from '@view/shared/molecules/search-controls/search-controls.component';
import { AddSampleComponent } from '../add-sample/legacy/add-sample.component';
import { FloatingSidebarComponent } from '@view/shared/molecules/floating-sidebar/floating-sidebar.component';

@Component({
  selector: 'app-ingested-orders-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    PaginatorComponent,
    ButtonComponent,
    MetricsDashboardComponent,
    FrozenColumnsSidebarComponent,
    ToastComponent,
    SearchControlsComponent,
    FilterSidebarComponent,
    ActiveFiltersComponent,
    ExportConfirmationModalComponent,
    AddSampleComponent,
    FloatingSidebarComponent
  ],
  templateUrl: './ingested-orders-list.component.html',
  styleUrls: ['./ingested-orders-list.component.scss'],
  providers: [
    FrozenColumnsService,
    {
      provide: 'FROZEN_COLUMNS_STORAGE_KEY',
      useValue: 'sig-frozen-columns-ingested-orders'
    }
  ]
})
export class IngestedOrdersListComponent
  extends BaseOrdersListComponent
  implements OnDestroy
{
  orders: IngestedOrder[] = [];
  override pageSize = 10;
  override totalRecords = 0;

  @ViewChild(PaginatorComponent) paginator!: PaginatorComponent;

  override readonly ButtonTypeEnum = ButtonTypeEnum;
  override readonly ButtonSizeEnum = ButtonSizeEnum;

  override readonly ExportModalState = ExportModalState;

  private readonly ICON_DOCUMENTO = 'icon-documento';
  private readonly ICON_CARNET = 'icon-identificacion';
  private readonly ICON_CALENDARIO = 'icon-calendario-fecha';
  private readonly ICON_CLIPBOARD = 'icon-clipboard';
  private readonly ICON_FICHA_NOMBRE = 'icon-ficha-nombre';

  private readonly ALL_COLUMNS = [
    {
      key: 'examCode',
      label: 'Código examen',
      searchable: true,
      icon: this.ICON_CLIPBOARD
    },
    {
      key: 'examName',
      label: 'Examen solicitado',
      searchable: true,
      icon: this.ICON_CLIPBOARD
    },
    {
      key: 'admissionDate',
      label: 'Fecha ingreso',
      searchable: false,
      icon: this.ICON_CALENDARIO,
      type: 'date' as const,
      tooltip: {
        pipe: 'fullDateTime' as const,
        alwaysShow: true
      }
    },
    {
      key: 'admissionSite',
      label: 'Sede',
      searchable: true,
      icon: this.ICON_DOCUMENTO
    },
    {
      key: 'region',
      label: 'Regional',
      searchable: true,
      icon: this.ICON_DOCUMENTO
    },
    {
      key: 'identificationType',
      label: 'Tipo ID',
      searchable: true,
      icon: this.ICON_CARNET
    },
    {
      key: 'identificationNumber',
      label: 'ID Paciente',
      searchable: true,
      icon: this.ICON_CARNET
    },
    {
      key: 'patientFirstName',
      label: 'Nombres',
      searchable: true,
      icon: this.ICON_FICHA_NOMBRE
    },
    {
      key: 'patientLastName',
      label: 'Apellidos',
      searchable: true,
      icon: this.ICON_FICHA_NOMBRE
    },
    {
      key: 'caseNumber',
      label: 'Código de muestra',
      searchable: true,
      icon: this.ICON_CLIPBOARD
    }
  ];

  private readonly ALL_COLUMN_KEYS = this.ALL_COLUMNS.filter(
    (col) => col.key !== 'actions'
  ).map((col) => col.key);

  readonly availableColumns: ColumnOption[] = this.ALL_COLUMNS.filter(
    (col) => col.key !== 'actions'
  ).map((col) => ({
    key: col.key,
    label: col.label
  }));

  readonly frozenColumnsInOrder = computed(() => {
    return this.frozenColumnsService.getFrozenColumnsInOrder(
      this.ALL_COLUMN_KEYS
    );
  });

  readonly visibleColumnsComputed = computed(() => {
    const frozenInOrder = this.frozenColumnsService.getFrozenColumnsInOrder(
      this.ALL_COLUMN_KEYS
    );

    const nonFrozenInOrder =
      this.frozenColumnsService.getNonFrozenColumnsInOrder(
        this.ALL_COLUMN_KEYS
      );

    const orderedKeys = [...frozenInOrder, ...nonFrozenInOrder];

    return orderedKeys
      .map((key) => this.ALL_COLUMNS.find((col) => col.key === key))
      .filter((col): col is (typeof this.ALL_COLUMNS)[0] => col !== undefined);
  });

  VISIBLE_COLUMNS = this.ALL_COLUMNS;

  override tableActions: TableActionType[] = [];

  showAddSampleModal = signal(false);

  constructor(
    private readonly presenter: IngestedOrdersPresenter,
    public readonly frozenColumnsService: FrozenColumnsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly toastService: ToastService,
    private readonly router: Router
  ) {
    super();
    this.setupTableActions();
  }

  openAddSampleModal(): void {
    this.showAddSampleModal.set(true);
  }

  closeAddSampleModal(): void {
    this.showAddSampleModal.set(false);
    this.loadOrdersInternal().subscribe();
  }

  navigateToAddSample(): void {
    this.router.navigate(['/preclinical/add-sample']);
  }

  protected getOrderStatus(): OrderStatus {
    return OrderStatus.INGRESADA;
  }

  protected override loadMetrics(): void {
    this.metricsSubscription = this.getMetricsService()
      .getMetricsStream(this.getOrderStatus(), 60000)
      .subscribe((metrics) => {
        this.dashboardMetrics = [
          {
            label: 'Hoy',
            value: 'Hoy',
            isDate: true,
            icon: 'icon-calendario-fecha'
          },
          { label: 'Total ordenes', value: metrics.totalOrders },
          { label: 'Ordenes ingresadas', value: metrics.ordersToday }
        ];
      });
  }

  protected getMetricsService(): IOrderMetricsPort {
    return {
      getMetrics: () => this.presenter.getMetrics(),
      getMetricsStream: (status: OrderStatus, interval: number) =>
        this.presenter.getMetricsStream(interval)
    };
  }

  protected getCdr(): ChangeDetectorRef {
    return this.cdr;
  }

  protected getToastService(): ToastService {
    return this.toastService;
  }

  getFilterOptionsService(): IFilterOptionsQueryPort {
    return {
      getFilterOptions: () => this.presenter.getFilterOptions()
    };
  }

  protected loadOrdersInternal(): Observable<void> {
    const queryParams = this.addFilterParamsToQuery(this.buildBaseQueryParams());

    return this.presenter.getOrders(queryParams).pipe(
      tap((response) => {
        this.orders = response.orders;
        this.totalRecords = response.pagination.total;
        this.cdr.detectChanges();
      }),
      map(() => void 0),
      catchError((error) => {
        console.error('Error loading orders:', error);
        return EMPTY;
      })
    );
  }

  protected resetToFirstPageAndReload(): void {
    if (this.paginator) {
      this.paginator.specificPage(0);
    } else {
      this.currentPage = 0;
      this.loadOrdersInternal().subscribe();
    }
  }

  onPageChange(pagination: {
    page: number;
    pageSize: number;
    total: number;
  }): void {
    this.currentPage = pagination.page;
    this.pageSize = pagination.pageSize;
    this.loadOrdersInternal().subscribe();
  }

  handleExportConfirm(): void {
    this._exportModalState.set(ExportModalState.DOWNLOADING);

    const queryParams: IngestedOrdersQueryParams =
      this.buildBaseExportQueryParams();
    this.addSearchParamsToExport(queryParams);
    this.addFilterParamsToExport(queryParams);

    this.presenter.exportOrders(queryParams).subscribe({
      next: (blob) => {
        const filename = `ordenes-ingresadas-${new Date().toISOString().split('T')[0]}.csv`;

        this.downloadFile(blob, filename);

        this.closeExportModal();

        this.toastService.setToast({
          title: 'Descarga exitosa',
          type: ToastTypeEnum.SUCCESS
        });

        setTimeout(() => {
          this.toastService.setToast(null);
        }, 15000);
      },
      error: (error) => {
        console.error('Export failed:', error);

        this.closeExportModal();

        this.toastService.setToast({
          title: 'Fallo al exportar el archivo',
          type: ToastTypeEnum.ERROR
        });

        setTimeout(() => {
          this.toastService.setToast(null);
        }, 15000);
      }
    });
  }

  private setupTableActions(): void {
    this.tableActions = [];
  }

  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.toastService.setToast(null);
  }
}
